/**
 * Constants for template editor
 * Centralized constants for variables, loops, and conditions
 */

import { TextAlignment, TextBlock, type IVariableFormatting } from '../types';

// Thymeleaf prefix for variable access
export const VARIABLE_PREFIX = "['@'].";

// Default date formats for date functions
export const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
export const DEFAULT_DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm';

// CSS classes for highlighting template elements
export const VARIABLE_HIGHLIGHT_CLASS = 'template-variable-highlight';
export const LINK_HIGHLIGHT_CLASS = 'template-link-highlight';
export const LOOP_HIGHLIGHT_CLASS = 'template-loop-highlight';
export const CONDITION_HIGHLIGHT_CLASS = 'template-condition-highlight';
export const FUNCTION_HIGHLIGHT_CLASS = 'template-function-highlight';
export const ROW_CONDITION_INDICATOR_CLASS = 'template-row-condition-indicator';

// HTML attributes
export const TH_REMOVE_ATTR = 'th:remove';
export const CONTENTEDITABLE_ATTR = 'contenteditable';

// Loop markers
export const LOOP_START_MARKER = '#loop';
export const LOOP_END_MARKER = '/loop';

// Regex patterns
export const IDENTIFIER_PATTERN = '[a-zA-Z_][\\w]*';
export const PATH_PATTERN = '[a-zA-Z_][\\w.]*';
export const LOOP_PATH_PATTERN = '[a-zA-Z_][\\w.]*(?:\\[[^\\]]*\\])*';

/**
 * Escape special regex characters in string
 */
export const escapeRegex = (str: string): string =>
  str.replace(/\[/g, '\\[').replace(/\]/g, '\\]').replace(/\./g, '\\.');

/**
 * Escape double quotes for use inside an HTML attribute value (e.g. data-var="...").
 * A path can legitimately contain literal quote characters (e.g. "@".path), so any
 * value written into an attribute must go through this first.
 */
export const escapeHtmlAttr = (value: string): string =>
  value.replace(/"/g, '&quot;');

/**
 * JMESPath treats a bare `@` as the current-node operator, not a literal
 * character - there's no way to disable that, it's grammar, not config. So a
 * path meant to reach a real field literally named "@" (which is how the
 * root data context is wrapped for rendering) must use the quoted-identifier
 * form `"@"` in JMESPath. This is the one deliberate exception to "content is
 * fully verbatim": only a path starting with the literal `@.` gets this
 * treatment, and only when writing into a data-* attribute - what the user
 * sees in the editor (and in the tree picker) always stays the bare `@.` form.
 */
export const toQuotedRootPath = (path: string): string =>
  path.startsWith('@.') ? `"@".${path.slice(2)}` : path;

/**
 * Inverse of toQuotedRootPath - used when reading a value back out of a
 * data-* attribute for display (e.g. pre-filling an edit modal).
 */
export const toBareRootPath = (path: string): string =>
  path.startsWith('"@".') ? `@.${path.slice(4)}` : path;

/**
 * Create regex for loop start markers (absolute paths)
 * Matches: {{#loop item : @.path}}
 */
export const createLoopStartRegex = (): RegExp =>
  new RegExp(
    `<[^>]*${TH_REMOVE_ATTR}[^>]*>\\s*\\{\\{${LOOP_START_MARKER}\\s+(${IDENTIFIER_PATTERN})\\s*:\\s*@\\.(${LOOP_PATH_PATTERN})\\}\\}\\s*<\\/[^>]+>`,
    'g'
  );

/**
 * Create regex for loop start markers (relative paths)
 * Matches: {{#loop item : parent.path}}
 */
export const createRelativeLoopStartRegex = (): RegExp =>
  new RegExp(
    `<[^>]*${TH_REMOVE_ATTR}[^>]*>\\s*\\{\\{${LOOP_START_MARKER}\\s+(${IDENTIFIER_PATTERN})\\s*:\\s*(${IDENTIFIER_PATTERN}\\.${LOOP_PATH_PATTERN})\\}\\}\\s*<\\/[^>]+>`,
    'g'
  );

/**
 * Create regex for loop end markers
 * Matches: {{/loop}}
 */
export const createLoopEndRegex = (): RegExp =>
  new RegExp(
    `<[^>]*${TH_REMOVE_ATTR}[^>]*>\\s*\\{\\{${LOOP_END_MARKER}\\s*\\}\\}\\s*<\\/[^>]+>`,
    'g'
  );

/**
 * Create regex for variable patterns
 * Matches: {{@.path}} or {{path}}
 */
export const createVariableRegex = (): RegExp =>
  /\{\{\s*(\[\'@\'\]\.)?([\w.]+)\s*\}\}/g;

/**
 * Condition operators supported in hybrid templates
 */
export const CONDITION_OPERATORS = {
  EQUALS: '==',
  NOT_EQUALS: '!=',
  GREATER_THAN: '>',
  GREATER_THAN_OR_EQUAL: '>=',
  LESS_THAN: '<',
  LESS_THAN_OR_EQUAL: '<=',
  CONTAINS: 'contains',
  NOT_CONTAINS: '!contains',
  STARTS_WITH: 'startsWith',
  ENDS_WITH: 'endsWith',
  IS_EMPTY: 'isEmpty',
  IS_NOT_EMPTY: '!isEmpty',
} as const;

/**
 * Condition types for UI dropdown
 */
export enum ConditionType {
  Simple = 'simple',
  Comparison = 'comparison',
  Negation = 'negation',
}

/**
 * Value types for condition comparisons
 */
export enum ConditionValueType {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Null = 'null',
}

/**
 * Loop template formats
 */
export enum LoopFormat {
  Plain = 'plain',
  BulletList = 'bullet_list',
  NumberedList = 'numbered_list',
  Table = 'table',
  DefinitionList = 'definition_list',
}

/**
 * Supported template functions
 */
export const TEMPLATE_FUNCTIONS = {
  UPPERCASE: 'uppercase',
  LOWERCASE: 'lowercase',
  FORMAT_DATE: 'formatDate',
  FORMAT_DATE_TIME: 'formatDateTime',
} as const;

/**
 * Map display function names to Thymeleaf utility names
 */
export const FUNCTION_THYMELEAF_MAP = {
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  formatDate: 'formatDate',
  formatDateTime: 'formatDateTime',
} as const;

const SYSTEM_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif";

export const DEFAULT_FORMATTING: IVariableFormatting = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  superscript: false,
  subscript: false,
  alignment: TextAlignment.Left,
  textBlock: TextBlock.Paragraph,
  fontFamily: SYSTEM_FONT_FAMILY,
  fontSize: '10.5pt',
};

export const TEXT_BLOCK_OPTIONS = [
  { value: TextBlock.Paragraph, label: 'Paragraph' },
  { value: TextBlock.Heading1, label: 'Heading 1' },
  { value: TextBlock.Heading2, label: 'Heading 2' },
  { value: TextBlock.Heading3, label: 'Heading 3' },
  { value: TextBlock.Heading4, label: 'Heading 4' },
  { value: TextBlock.Heading5, label: 'Heading 5' },
  { value: TextBlock.Heading6, label: 'Heading 6' },
];

export const TEXT_BLOCK_FONT_SIZES: Record<TextBlock, string> = {
  [TextBlock.Paragraph]: '14px',
  [TextBlock.Heading1]: '32px',
  [TextBlock.Heading2]: '24px',
  [TextBlock.Heading3]: '20px',
  [TextBlock.Heading4]: '16px',
  [TextBlock.Heading5]: '14px',
  [TextBlock.Heading6]: '12px',
};

export const FONT_FAMILY_OPTIONS = [
  { value: SYSTEM_FONT_FAMILY, label: 'System UI' },
  { value: "'andale mono', monospace", label: 'Andale Mono' },
  { value: 'arial, helvetica, sans-serif', label: 'Arial' },
  { value: "'arial black', sans-serif", label: 'Arial Black' },
  { value: "'book antiqua', palatino, serif", label: 'Book Antiqua' },
  { value: "'comic sans ms', sans-serif", label: 'Comic Sans MS' },
  { value: "'courier new', courier, monospace", label: 'Courier New' },
  { value: 'georgia, palatino, serif', label: 'Georgia' },
  { value: 'helvetica, arial, sans-serif', label: 'Helvetica' },
  { value: 'impact, sans-serif', label: 'Impact' },
  { value: 'symbol', label: 'Symbol' },
  { value: 'tahoma, arial, helvetica, sans-serif', label: 'Tahoma' },
  { value: 'terminal, monaco, monospace', label: 'Terminal' },
  { value: "'times new roman', times, serif", label: 'Times New Roman' },
  { value: "'trebuchet ms', geneva, sans-serif", label: 'Trebuchet MS' },
  { value: 'verdana, geneva, sans-serif', label: 'Verdana' },
  { value: 'webdings', label: 'Webdings' },
  { value: "wingdings, 'zapf dingbats'", label: 'Wingdings' },
];

const normalizeFontFamily = (value: string): string =>
  value
    .toLowerCase()
    .replace(/["']/g, '')
    .replace(/\s*,\s*/g, ',')
    .trim();

/**
 * Maps a font-family value read from the DOM (browser-normalized, TinyMCE or
 * legacy unquoted format) to the matching FONT_FAMILY_OPTIONS value.
 * Returns the input unchanged when no option matches.
 */
export const matchFontFamilyOption = (value: string): string =>
  FONT_FAMILY_OPTIONS.find(
    option => normalizeFontFamily(option.value) === normalizeFontFamily(value)
  )?.value ?? value;

export const FONT_SIZE_OPTIONS = [
  { value: '8pt', label: '8pt' },
  { value: '10pt', label: '10pt' },
  { value: '10.5pt', label: '10.5pt' },
  { value: '12pt', label: '12pt' },
  { value: '14pt', label: '14pt' },
  { value: '18pt', label: '18pt' },
  { value: '24pt', label: '24pt' },
  { value: '36pt', label: '36pt' },
];

export const SYSTEM_VARIABLE_NAMES = [
  'current_date',
  'current_time',
  'current_date_time',
];

export const FUNCTION_OPTIONS = [
  { label: 'Uppercase', value: 'uppercase' },
  { label: 'Lowercase', value: 'lowercase' },
  { label: 'Format Date', value: 'formatDate' },
  { label: 'Format DateTime', value: 'formatDateTime' },
];

export const DATE_FORMAT_PRESETS = [
  { label: 'dd.MM.yyyy. (17.07.2025.)', value: 'dd.MM.yyyy.' },
  { label: 'MM/dd/yyyy (07/17/2025)', value: 'MM/dd/yyyy' },
  { label: 'yyyy-MM-dd (2025-07-17)', value: 'yyyy-MM-dd' },
  { label: 'dd.MM.yyyy. HH:mm (17.07.2025. 14:30)', value: 'dd.MM.yyyy. HH:mm' },
];
